var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { isPlatformBrowser } from '@angular/common';
import { Auth } from '@freelancer/auth';
import * as djv from 'djv';
import * as Rx from 'rxjs';
import { distinctUntilChanged, map, publishReplay, refCount, switchMap, } from 'rxjs/operators';
import { definitions, topLevelSchemas } from './json-schemas';
var LocalStorage = /** @class */ (function () {
    function LocalStorage(auth, platformId) {
        this.auth = auth;
        this.platformId = platformId;
        this.env = djv();
        this.LOGGED_OUT_KEY = 'logged-out';
        this.isInitialized = false;
    }
    /*
     * Initialisation only occurs when the functions are being called,
     * allowing for no side effects during injection
     * http://misko.hevery.com/code-reviewers-guide/flaw-constructor-does-real-work/
     */
    LocalStorage.prototype.init = function () {
        var _this = this;
        this.subjects = {};
        this.authId$ = this.auth.authState$.pipe(map(function (authState) {
            return authState ? "id-" + authState.userId : _this.LOGGED_OUT_KEY;
        }), publishReplay(1), refCount());
        this.authId$.subscribe(function (id) {
            _this.authId = id;
        });
        this.env.addSchema('defs.json', definitions);
        /*
          This event will only be fired in tabs other than the current one.
          See https://www.w3.org/TR/webstorage/#the-localstorage-attribute
    
          If other tabs have an observable of the changed item, the observable will emit the change.
        */
        window.addEventListener('storage', function (e) {
            if (!e.key) {
                return;
            }
            if (_this.subjects[e.key] && _this.subjects[e.key][_this.authId]) {
                var newObject = void 0;
                try {
                    newObject = JSON.parse(e.newValue || '{}');
                }
                catch (err) {
                    return;
                }
                _this.subjects[e.key][_this.authId].next(newObject[_this.authId] && newObject[_this.authId].item);
            }
        });
        this.isInitialized = true;
    };
    /**
     * Returns the current item under `key` for the current authId
     * or under the `loggedOutKey` if `loggedOutMode` is true.
     * (Non-observable version)
     */
    LocalStorage.prototype.get = function (key, loggedOutMode) {
        if (!isPlatformBrowser(this.platformId)) {
            return undefined;
        }
        if (!this.isInitialized) {
            this.init();
        }
        var authKey = loggedOutMode ? this.LOGGED_OUT_KEY : this.authId;
        var entryObj = getEntryObject(key, authKey);
        if (!entryObj || !entryObj[authKey]) {
            // if parsing error occurred, or the key did not exist yet.
            return undefined;
        }
        var item = entryObj[authKey].item;
        // DJV CHECK
        if (!this.isValidItem(key, item)) {
            return undefined;
        }
        return item;
    };
    /**
     * Returns an OBSERVABLE of the current item under `key` for the current authId
     * or under the `loggedOutKey` if `loggedOutMode` is true.
     */
    LocalStorage.prototype.getObservable = function (key, loggedOutMode) {
        var _this = this;
        if (!isPlatformBrowser(this.platformId)) {
            return Rx.of(undefined);
        }
        if (!this.isInitialized) {
            this.init();
        }
        return this.authId$.pipe(switchMap(function (authId) {
            var authKey = loggedOutMode ? _this.LOGGED_OUT_KEY : authId;
            var entryObj = getEntryObject(key, authKey);
            if (!entryObj) {
                // parsing error occured
                return Rx.of(undefined);
            }
            // item obtained does not conform to allowed LocalStorageTypes
            if (entryObj[authId] && !_this.isValidItem(key, entryObj[authId].item)) {
                return Rx.of(undefined);
            }
            if (!_this.subjects[key]) {
                _this.subjects[key] = {};
            }
            if (!_this.subjects[key][authKey]) {
                // CREATE SUBJECT
                _this.subjects[key][authKey] = new Rx.BehaviorSubject(entryObj[authKey] ? entryObj[authKey].item : undefined);
            }
            return _this.subjects[key][authKey].asObservable().pipe(distinctUntilChanged());
        }));
    };
    /**
     * Stores `item` in local storage for `key` under the current authId
     * or under the `loggedOutKey` if `loggedOutMode` is true.
     * If there is a subject for that particular entry, notify the observers (of that subject) of the change.
     */
    LocalStorage.prototype.set = function (key, item, loggedOutMode) {
        if (!isPlatformBrowser(this.platformId)) {
            return;
        }
        if (!this.isInitialized) {
            this.init();
        }
        var authKey = loggedOutMode ? this.LOGGED_OUT_KEY : this.authId;
        setRaw(key, authKey, item);
        if (this.subjects[key] && this.subjects[key][authKey]) {
            this.subjects[key][authKey].next(item);
        }
    };
    /**
     * Checks whether the item is valid against the JSON Schema.
     */
    LocalStorage.prototype.isValidItem = function (key, item) {
        this.env.addSchema(key, topLevelSchemas[key]);
        if (this.env.validate(key, item)) {
            console.warn("Failed to load " + key + " from local storage as its format has changed. Check that it still validates against the schema at 'local-storage/json-schemas.ts'");
            return false;
        }
        return true;
    };
    return LocalStorage;
}());
export { LocalStorage };
/* HELPER FUNCTIONS */
/**
 * Directly store a @param item for a given @param authId.
 */
function setRaw(key, authId, item) {
    var entryObj = getEntryObject(key, authId);
    if (!entryObj) {
        // parsing error occurred.
        return;
    }
    var currentTime = Date.now();
    var addItem = entryObj[authId]
        ? __assign({}, entryObj[authId], { timeUpdated: currentTime, item: item }) : {
        timeUpdated: currentTime,
        timeCreated: currentTime,
        lastReadTime: currentTime,
        item: item,
    };
    entryObj[authId] = addItem;
    // if doesn't exist, creates, otherwise overrides.
    window.localStorage.setItem(key, JSON.stringify(entryObj));
}
/**
 * Turns the string when you first index into local storage (via `key`)
 * into a JSON object and updates `lastReadTime` for the particular `authId`.
 */
function getEntryObject(key, authId) {
    var entryObj;
    try {
        entryObj = JSON.parse(window.localStorage.getItem(key) || '{}');
    }
    catch (e) {
        return null;
    }
    // update `lastReadTime`
    if (entryObj[authId]) {
        entryObj[authId] = __assign({}, entryObj[authId], { lastReadTime: Date.now() });
        window.localStorage.setItem(key, JSON.stringify(entryObj));
    }
    return entryObj;
}
